import { useCallback, useMemo, useState, useEffect } from 'react';
import { useTranslate } from 'react-admin';
import {
    Box,
    Button,
    Chip,
    Grid,
    Paper,
    Popover,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
    IconButton,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import ErrorPage403 from 'src/pages/error/403';
import { KpiCard } from '../../components/KpiCard';
import { ReferralDetailDialog } from '../../components/ReferralDetailDialog';
import CopyCell from '../../components/CopyCell';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import {
    CELL_COLOR,
    METHOD_CHIPS,
    RANGE_LABELS,
    RANGE_LABEL_MAP,
    REFERRAL_PRODUCT_TABS,
    TABLE_COLUMNS,
    TAB_LABELS,
    USER_TYPE_CHIPS,
    formatDate,
    formatUtc8Date,
} from '../../mock-data';
import type { DashboardV3ReferralTableColumnKey } from '../../mock-data';
import type {
    MethodFilter,
    RangeValue,
    ReferralProductType,
    UserTypeFilter,
} from '../../services/dashboardV3.types';
import { useDashboardV3ReferralData } from '../../hooks/useDashboardV3ReferralData';


type ColumnKey = DashboardV3ReferralTableColumnKey;

function formatUSD(value?: number) {
    return `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function formatInt(value?: number) {
    return Number(value || 0).toLocaleString();
}


function formatDelta(value?: number | null) {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return '--'
    }

    return `${value > 0 ? '+' : ''}${value}%`
}

function formatCellValue(key: ColumnKey, value: any) {
    if (value === undefined || value === null || value === '') return '-';

    if (
        key === 'downlineVolume' ||
        key === 'downlineDeposit' ||
        key === 'totalFees' ||
        key === 'rebateIncome'
    ) {
        return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    return value;
}

export const ReferralPage = () => {
    const t = useTranslate();
    const [range, setRange] = useState<RangeValue>('7d');
    const [customFrom, setCustomFrom] = useState(() => formatUtc8Date(new Date(Date.now() - 7 * 86400000)));
    const [customTo, setCustomTo] = useState(() => formatUtc8Date(new Date()));

    const [methodFilter, setMethodFilter] = useState<MethodFilter>('all');
    const [userTypeFilter, setUserTypeFilter] = useState<UserTypeFilter>('all');

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [productType, setProductType] = useState<ReferralProductType>('perps');

    const [sortKey, setSortKey] = useState<ColumnKey>('code');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

    const [dialogId, setDialogId] = useState('');
    const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'deposit' | 'trading' | 'retention' | 'referral'>('referral');
    const [customAnchorEl, setCustomAnchorEl] = useState<HTMLElement | null>(null);

    const [draftUserId, setDraftUserId] = useState('');
    const [draftWalletAddress, setDraftWalletAddress] = useState('');
    const [draftInvitationCode, setDraftInvitationCode] = useState('');
    const [draftLoginAccount, setDraftLoginAccount] = useState('');

    const [userIdInput, setUserIdInput] = useState('');
    const [walletAddressInput, setWalletAddressInput] = useState('');
    const [loginAccountInput, setLoginAccountInput] = useState('');
    const [invitationCodeInput, setInvitationCodeInput] = useState('');

    const userId = useDebouncedValue(userIdInput.trim(), 500);
    const walletAddress = useDebouncedValue(walletAddressInput.trim(), 500);
    const loginAccount = useDebouncedValue(loginAccountInput.trim(), 500);
    const invitationCode = useDebouncedValue(invitationCodeInput.trim(), 500);

    const hasSearchValue = Boolean(
        userIdInput || walletAddressInput || loginAccountInput || invitationCodeInput
    );

    const [largeDatasetAnchorEl, setLargeDatasetAnchorEl] = useState<HTMLElement | null>(null);

    const largeDatasetPopoverOpen = Boolean(largeDatasetAnchorEl);

    const handleOpenLargeDatasetPopover = (event: React.MouseEvent<HTMLElement>) => {
        setLargeDatasetAnchorEl(event.currentTarget);
    };

    const handleCloseLargeDatasetPopover = () => {
        setLargeDatasetAnchorEl(null);
    };

    const PRODUCT_TAB_COLORS: Record<string, string> = {
        perps: '#FFB74D',
        prediction_spot: '#03DAC6',
        prediction_leverage: '#BB86FC',
    };

    const getProductTabColor = (type: string) => {
        return PRODUCT_TAB_COLORS[type] ?? '#FFB74D';
    };

    const currentProductAccent = getProductTabColor(productType);

    useEffect(() => {
        setPage(0);
    }, [userId, walletAddress, loginAccount, invitationCode]);


    const queryParams = useMemo(
        () => ({
            range,
            startDate: range === 'custom' ? customFrom : undefined,
            endDate: range === 'custom' ? customTo : undefined,
            page,
            size: rowsPerPage,
            loginMethod: methodFilter,
            userType: userTypeFilter,
            sortBy: sortKey,
            sortOrder: sortDirection,
            detailId: dialogId,
            userId,
            walletAddress,
            invitationCode,
            loginAccount,
            productType,
        }),
        [
            range,
            customFrom,
            customTo,
            page,
            rowsPerPage,
            methodFilter,
            userTypeFilter,
            sortKey,
            sortDirection,
            dialogId,
            userId,
            walletAddress,
            invitationCode,
            loginAccount,
            productType,
        ]
    );

    const {
        kpi,
        list,
        total,
        detail,
        kpiLoading,
        usersLoading,
        detailLoading,
        exporting,
        exportUsersCsv,
        exportSubsCsv,
    } = useDashboardV3ReferralData(queryParams);


    const rangeText = t(RANGE_LABEL_MAP[range], { _: '' });

    const COPYABLE_COLUMNS: Partial<Record<ColumnKey, boolean>> = {
        walletAddr: true,
        loginAccount: true,
        userId: true,
        code: true,
        parentCode: true,
        topCode: true,
    };

    const handleRangeChange = useCallback((v: RangeValue) => {
        setRange(v);
    }, []);

    const handleMethodFilterChange = useCallback((v: MethodFilter) => {
        setMethodFilter(v);
        setPage(0);
    }, []);

    const handleUserTypeFilterChange = useCallback((v: UserTypeFilter) => {
        setUserTypeFilter(v);
        setPage(0);
    }, []);

    const handleSort = useCallback(
        (key: ColumnKey) => {
            if (sortKey === key) {
                setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            } else {
                setSortKey(key);
                setSortDirection('asc');
            }

            setPage(0);
        },
        [sortKey]
    );

    const handlePageChange = useCallback((_: unknown, newPage: number) => {
        setPage(newPage);
    }, []);

    const handleRowsPerPageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number(e.target.value));
        setPage(0);
    }, []);

    const handleRowClick = useCallback((row: any) => {
        setDialogId(row.code || '');
    }, []);

    const handleDialogClose = useCallback(() => {
        setDialogId('');
    }, []);

    const handleTabChange = useCallback((tab: typeof activeTab) => {
        setActiveTab(tab);
        setPage(0);
    }, []);

    const handleCustomChipClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setRange('custom');
        setCustomAnchorEl(event.currentTarget);
    }, []);

    const handleCustomPopoverClose = useCallback(() => {
        setCustomAnchorEl(null);
    }, []);

    const handleApplyCustom = useCallback(() => {
        handleCustomPopoverClose();
    }, [handleCustomPopoverClose]);

    // const handleSearch = useCallback(() => {
    //     setUserId(draftUserId.trim());
    //     setWalletAddress(draftWalletAddress.trim());
    //     setInvitationCode(draftInvitationCode.trim());
    //     setLoginAccount(draftLoginAccount.trim());
    //     setPage(0);
    // }, [draftUserId, draftWalletAddress, draftInvitationCode, draftLoginAccount]);

    // const handleResetSearch = useCallback(() => {
    //     setDraftUserId('');
    //     setDraftWalletAddress('');
    //     setDraftInvitationCode('');
    //     setDraftLoginAccount('');
    //     setUserId('');
    //     setWalletAddress('');
    //     setInvitationCode('');
    //     setLoginAccount('');
    //     setPage(0);
    // }, []);

    const handleClearSearch = useCallback(() => {
        setUserIdInput('');
        setWalletAddressInput('');
        setLoginAccountInput('');
        setInvitationCodeInput('');
        setPage(0);
    }, []);

    const getRangeShortText = (value: RangeValue) =>
        t(`module.dashboardV3.rangeShort.${value}`, {
            _: value === '7d'
                ? '7D'
                : value === '30d'
                    ? '30D'
                    : value === 'month'
                        ? 'This Month'
                        : value === 'custom'
                            ? 'Custom'
                            : 'All',
        });

    const getRangeCompareText = (value: RangeValue) =>
        t(`module.dashboardV3.rangeCompare.${value}`, { _: '' });

    const buildKpiTitle = (label: string) => `${label} · ${getRangeShortText(range)}`;

    const getVolumeTitle = () => {
        if (productType === 'prediction_spot') {
            return t('module.dashboardV3.kpi.predictionVolume', {
                _: 'Subordinate Prediction Spot Volume',
            });
        }

        if (productType === 'prediction_leverage') {
            return t('module.dashboardV3.kpi.predictionLeverageVolume', {
                _: 'Subordinate Prediction Leverage Volume',
            });
        }

        return t('module.dashboardV3.kpi.contractVolume', {
            _: 'Subordinate Perpetuals Volume',
        });
    };

    const getVolumeSubtitle = () => {
        if (productType === 'prediction_spot') {
            return t('module.dashboardV3.kpi.predictionVolumeSubtitle', {
                _: 'Subordinate volume in Prediction Spot',
            });
        }

        if (productType === 'prediction_leverage') {
            return t('module.dashboardV3.kpi.predictionLeverageVolumeSubtitle', {
                _: 'Subordinate volume in Prediction Leverage',
            });
        }

        return t('module.dashboardV3.kpi.contractVolumeSubtitle', {
            _: 'Subordinate volume in Perpetuals',
        });
    };

    const searchInputSx = {
        flexShrink: 0,

        '& .MuiOutlinedInput-root': {
            height: 36,
            minHeight: 36,
            borderRadius: '4px',
            bgcolor: 'transparent',
            color: '#FFFFFF',
            fontSize: 14,
            lineHeight: '36px',
            boxSizing: 'border-box',
        },

        '& .MuiOutlinedInput-input': {
            height: 36,
            minHeight: 36,
            lineHeight: '36px',
            padding: '0 14px',
            boxSizing: 'border-box',
            fontSize: 14,

            '&::placeholder': {
                color: 'rgba(255,255,255,0.45)',
                opacity: 1,
            },
        },

        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.16)',
        },

        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(187,134,252,0.55)',
        },

        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#BB86FC !important',
            borderWidth: '1px !important',
        },
    } as const;

    return (
        <>
            {/* <PermissionGuard resource="dashboard-v3" action="read" fallback=''>
                <Box
                    sx={{
                        background: '#1F1B33',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
                        display: 'flex',
                        position: 'sticky',
                        top: 64,
                        zIndex: 90,
                        overflowX: 'auto',
                        px: 1,
                        py: 0.5,
                        mt: 2,
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}
                >
                    {TAB_LABELS.map((tab) => {
                        const active = activeTab === tab.value;
                        return (
                            <Box
                                key={tab.value}
                                onClick={() => handleTabChange(tab.value as typeof activeTab)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 120,
                                    height: 48,
                                    px: 2,
                                    fontSize: 14,
                                    fontWeight: 500,
                                    letterSpacing: '1.25px',
                                    textTransform: 'uppercase',
                                    color: active ? '#BB86FC' : 'rgba(187,134,252,0.6)',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    userSelect: 'none',
                                    whiteSpace: 'nowrap',
                                    '&:hover': { background: 'rgba(187,134,252,0.08)' },
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: 2,
                                        bgcolor: '#BB86FC',
                                        transform: active ? 'scaleX(1)' : 'scaleX(0)',
                                        transformOrigin: 'center',
                                        transition: 'transform .2s',
                                    },
                                }}
                            >
                                {t(tab.labelKey, { _: tab.labelKey })}
                            </Box>
                        );
                    })}
                </Box>
            </PermissionGuard> */}


            <PermissionGuard resource="dashboard-v3" action="read" fallback={<ErrorPage403 />}>

                <DashboardContent sx={{ pb: 0, bgcolor: '#121212' }}>
                    <Box sx={{ mt: 3, color: '#FFFFFF' }}>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={2}
                        >
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                    {t('module.dashboardV3.title', { _: 'Referral Data' })}
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.64)', mb: 3 }}>
                                    {t('module.dashboardV3.subtitle', { _: 'Referral · USDC' })}
                                </Typography>
                            </Box>

                            <Paper sx={{ bgcolor: '#121212', p: 3, }}>
                                <Stack alignItems="center" spacing={2}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {range === 'all' ? (
                                            <>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleOpenLargeDatasetPopover}
                                                    sx={{
                                                        ml: 0.5,
                                                        mt: 0.4,
                                                        width: 28,
                                                        height: 28,
                                                        color: '#FFB74D',
                                                        border: '1px solid rgba(255,183,77,0.35)',
                                                        bgcolor: 'rgba(255,183,77,0.08)',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(255,183,77,0.16)',
                                                            borderColor: 'rgba(255,183,77,0.6)',
                                                        },
                                                    }}
                                                >
                                                    <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
                                                </IconButton>

                                                <Popover
                                                    open={largeDatasetPopoverOpen}
                                                    anchorEl={largeDatasetAnchorEl}
                                                    onClose={handleCloseLargeDatasetPopover}
                                                    anchorOrigin={{
                                                        vertical: 'bottom',
                                                        horizontal: 'right',
                                                    }}
                                                    transformOrigin={{
                                                        vertical: 'top',
                                                        horizontal: 'right',
                                                    }}
                                                    PaperProps={{
                                                        sx: {
                                                            mt: 1,
                                                            px: 1.5,
                                                            py: 1,
                                                            maxWidth: 280,
                                                            bgcolor: '#232323',
                                                            color: '#FFB74D',
                                                            border: '1px solid rgba(255,183,77,0.28)',
                                                            boxShadow: '0 12px 28px rgba(0,0,0,0.35)',
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: 13,
                                                            lineHeight: 1.5,
                                                        }}
                                                    >
                                                        {t('module.dashboardV3.largeDatasetWarning', {
                                                            _: 'Large dataset — loading may be slow',
                                                        })}
                                                    </Typography>
                                                </Popover>
                                            </>
                                        ) : null}

                                        {RANGE_LABELS.map((item) => {
                                            const isCustom = item.value === 'custom';

                                            return (
                                                <Chip
                                                    key={item.value}
                                                    label={t(item.labelKey, { _: item.labelKey })}
                                                    size="medium"
                                                    clickable
                                                    onClick={(event) => {
                                                        if (isCustom) {
                                                            handleCustomChipClick(event);
                                                        } else {
                                                            handleRangeChange(item.value);
                                                            handleCustomPopoverClose();
                                                        }
                                                    }}
                                                    variant="outlined"
                                                    sx={{
                                                        borderRadius: 2,
                                                        borderColor: range === item.value ? '#BB86FC' : 'rgba(255,255,255,.12)',
                                                        bgcolor: range === item.value ? 'rgba(187, 134, 252, .16)' : 'transparent',
                                                        color: range === item.value ? '#BB86FC' : 'rgba(255,255,255,.60)',
                                                    }}
                                                />
                                            );
                                        })}


                                    </Box>



                                    <Popover
                                        open={Boolean(customAnchorEl)}
                                        anchorEl={customAnchorEl}
                                        onClose={handleCustomPopoverClose}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                        PaperProps={{
                                            sx: {
                                                bgcolor: '#10101A',
                                                border: '1px solid rgba(255,255,255,0.08)',
                                                p: 2,
                                                mt: 1,
                                                minWidth: 320,
                                            },
                                        }}
                                    >
                                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                                            <TextField
                                                type="date"
                                                size="small"
                                                value={customFrom}
                                                onChange={(e) => setCustomFrom(e.target.value)}
                                                sx={{
                                                    bgcolor: '#141422',
                                                    borderRadius: 1,
                                                    width: 150,
                                                    '& .MuiInputBase-input': { color: '#FFFFFF' },
                                                }}
                                            />

                                            <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>—</Typography>

                                            <TextField
                                                type="date"
                                                size="small"
                                                value={customTo}
                                                onChange={(e) => setCustomTo(e.target.value)}
                                                sx={{
                                                    bgcolor: '#141422',
                                                    borderRadius: 1,
                                                    width: 150,
                                                    '& .MuiInputBase-input': { color: '#FFFFFF' },
                                                }}
                                            />

                                            <Button
                                                variant="contained"
                                                color="secondary"
                                                size="small"
                                                sx={{
                                                    textTransform: 'none',
                                                    bgcolor: 'rgba(187, 134, 252, .16)',
                                                    border: '1px solid #BB86FC',
                                                    color: '#BB86FC',
                                                }}
                                                onClick={handleApplyCustom}
                                            >
                                                {t('module.dashboardV3.actions.apply', { _: 'Apply' })}
                                            </Button>
                                        </Stack>
                                    </Popover>
                                </Stack>
                            </Paper>
                        </Stack>

                        <Stack
                            direction="row"
                            spacing={4}
                            sx={{
                                mt: 1,
                                mb: 2,
                                borderBottom: '1px solid rgba(255,255,255,0.12)',
                            }}
                        >
                            {REFERRAL_PRODUCT_TABS.map((tab) => {
                                const active = productType === tab.value;
                                const activeColor = getProductTabColor(tab.value);

                                return (
                                    <Box
                                        key={tab.value}
                                        onClick={() => {
                                            setProductType(tab.value);
                                            setPage(0);
                                            setDialogId('');
                                        }}
                                        sx={{
                                            py: 1.2,
                                            px: 0.5,
                                            cursor: 'pointer',
                                            color: active ? activeColor : 'rgba(255,255,255,0.56)',
                                            fontSize: 14,
                                            fontWeight: active ? 700 : 600,
                                            borderBottom: active ? `2px solid ${activeColor}` : '2px solid transparent',
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        {t(tab.labelKey, { _: tab.labelKey })}
                                    </Box>
                                );
                            })}
                        </Stack>

                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={3}>
                                <KpiCard
                                    title={buildKpiTitle(t('module.dashboardV3.kpi.invites', { _: 'Referrers' }))}
                                    value={formatInt(kpi?.invites)}
                                    delta={formatDelta(kpi?.invitesDelta)}
                                    deltaNote={getRangeCompareText(range)}
                                    subtitle={t('module.dashboardV3.kpi.invitesSubtitle', {
                                        _: 'Referrers with newly bound subordinates in the period',
                                    })}
                                    variant="primary"
                                    loading={kpiLoading}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <KpiCard
                                    title={buildKpiTitle(t('module.dashboardV3.kpi.fee', { _: 'Subordinate Fees' }))}
                                    value={formatUSD(kpi?.fee)}
                                    delta={formatDelta(kpi?.feeDelta)}
                                    deltaNote={getRangeCompareText(range)}
                                    subtitle={t('module.dashboardV3.kpi.feeSubtitle', {
                                        _: 'Total fees generated by subordinates in the period',
                                    })}
                                    variant="info"
                                    loading={kpiLoading}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <KpiCard
                                    title={buildKpiTitle(t('module.dashboardV3.kpi.rebate', { _: 'Rebate Income' }))}
                                    value={formatUSD(kpi?.rebate)}
                                    delta={formatDelta(kpi?.rebateDelta)}
                                    deltaNote={getRangeCompareText(range)}
                                    subtitle={t('module.dashboardV3.kpi.rebateSubtitle', {
                                        _: 'Rebates paid to referrers in the period',
                                    })}
                                    variant="secondary"
                                    valueColor="#03DAC6"
                                    loading={kpiLoading}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <KpiCard
                                    title={buildKpiTitle(getVolumeTitle())}
                                    value={formatUSD(kpi?.downlineVolume)}
                                    delta={formatDelta(kpi?.downlineVolumeDelta)}
                                    deltaNote={getRangeCompareText(range)}
                                    subtitle={getVolumeSubtitle()}
                                    variant="warning"
                                    accentColor={currentProductAccent}
                                    valueColor={currentProductAccent}
                                    loading={kpiLoading}
                                />
                            </Grid>
                        </Grid>

                        <Paper sx={{ bgcolor: '#232323', border: '1px solid rgba(255,255,255,0.08)', mb: 4 }}>
                            <Box
                                sx={{
                                    p: 2.25,
                                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1.5,
                                    alignItems: 'center',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>
                                        {t('module.dashboardV3.filters.loginMethod', { _: 'Login Method' })}
                                    </Typography>
                                    {METHOD_CHIPS.map((item) => (
                                        <Chip
                                            key={item.value}
                                            label={t(item.labelKey, { _: item.labelKey })}
                                            size="medium"
                                            clickable
                                            onClick={() => handleMethodFilterChange(item.value)}
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 2,
                                                borderColor: methodFilter === item.value ? '#BB86FC' : 'rgba(255,255,255,.12)',
                                                bgcolor: methodFilter === item.value ? 'rgba(187, 134, 252, .16)' : 'transparent',
                                                color: methodFilter === item.value ? '#BB86FC' : 'rgba(255,255,255,.60)',
                                            }}
                                        />
                                    ))}
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>
                                        {t('module.dashboardV3.filters.userType', { _: 'User Type' })}
                                    </Typography>
                                    {USER_TYPE_CHIPS.map((item) => (
                                        <Chip
                                            key={item.value}
                                            label={t(item.labelKey, { _: item.labelKey })}
                                            size="medium"
                                            clickable
                                            onClick={() => handleUserTypeFilterChange(item.value)}
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 2,
                                                borderColor: userTypeFilter === item.value ? '#BB86FC' : 'rgba(255,255,255,.12)',
                                                bgcolor: userTypeFilter === item.value ? 'rgba(187, 134, 252, .16)' : 'transparent',
                                                color: userTypeFilter === item.value ? '#BB86FC' : 'rgba(255,255,255,.60)',
                                            }}
                                        />
                                    ))}
                                </Box>

                                <Box sx={{ flex: 1 }} />


                                <PermissionButton resource="dashboard-v3" action="export">
                                    <Button
                                        variant="text"
                                        disabled={exporting}
                                        onClick={exportUsersCsv}
                                        sx={{ color: '#BB86FC', textTransform: 'none', minWidth: 0, px: 1.5 }}
                                    >
                                        <DownloadIcon fontSize="small" sx={{ mr: 0.5 }} />
                                        {exporting
                                            ? t('module.dashboardV3.detail.exporting', { _: 'Exporting...' })
                                            : t('module.dashboardV3.actions.exportCsv', { _: 'Export CSV' })}
                                    </Button>
                                </PermissionButton>
                            </Box>

                            <Box
                                sx={{
                                    px: 2,
                                    py: 2,
                                    borderTop: '1px solid rgba(255,255,255,0.08)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    flexWrap: 'wrap',
                                    bgcolor: '#232323',
                                }}
                            >
                                <TextField
                                    size="small"
                                    placeholder={t('module.dashboardV3.filters.userIdPlaceholder', { _: 'User ID' })}
                                    value={userIdInput}
                                    onChange={(e) => setUserIdInput(e.target.value)}
                                    sx={{ ...searchInputSx, width: 172 }}
                                />

                                <TextField
                                    size="small"
                                    placeholder={t('module.dashboardV3.filters.userAddressPlaceholder', { _: 'User Address / Parent Address' })}
                                    value={walletAddressInput}
                                    onChange={(e) => setWalletAddressInput(e.target.value)}
                                    sx={{ ...searchInputSx, width: 260 }}
                                />

                                <TextField
                                    size="small"
                                    placeholder={t('module.dashboardV3.filters.loginAccountPlaceholder', { _: 'Login Account' })}
                                    value={loginAccountInput}
                                    onChange={(e) => setLoginAccountInput(e.target.value)}
                                    sx={{ ...searchInputSx, width: 260 }}
                                />

                                <TextField
                                    size="small"
                                    placeholder={t('module.dashboardV3.filters.invitationCodePlaceholder', { _: 'Invitation Code / Parent Code / Top Code' })}
                                    value={invitationCodeInput}
                                    onChange={(e) => setInvitationCodeInput(e.target.value)}
                                    sx={{ ...searchInputSx, width: 340 }}
                                />

                                {hasSearchValue && (
                                    <Button
                                        variant="text"
                                        startIcon={<CloseIcon sx={{ fontSize: 12 }} />}
                                        onClick={handleClearSearch}
                                        sx={{
                                            height: 36,
                                            px: 1.5,
                                            color: '#BB86FC',
                                            fontSize: 14,
                                            fontWeight: 600,
                                            textTransform: 'none',
                                            '&:hover': {
                                                bgcolor: 'rgba(187,134,252,0.08)',
                                            },
                                        }}
                                    >
                                        {t('module.dashboardV3.actions.clear', { _: 'Clear' })}
                                    </Button>
                                )}
                            </Box>


                            <TableContainer sx={{ overflowX: 'auto', bgcolor: '#232323' }}>
                                <Table sx={{ minWidth: 1500, borderCollapse: 'separate' }}>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: 'rgba(255,255,255,0.04)' }}>
                                            {TABLE_COLUMNS.map((col) => (
                                                <TableCell
                                                    key={col.key}
                                                    align={col.align}
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.66)',
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap',
                                                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                                                        bgcolor: 'rgba(255,255,255,0.04)',
                                                    }}
                                                >
                                                    {col.sortable === false ? (
                                                        <span>{t(col.labelKey, { _: col.labelKey })}</span>
                                                    ) : (
                                                        <TableSortLabel
                                                            active={sortKey === col.key}
                                                            direction={sortKey === col.key ? sortDirection : 'asc'}
                                                            onClick={() => handleSort(col.key)}
                                                            sx={{
                                                                color: 'rgba(255,255,255,0.85) !important',
                                                                '& .MuiTableSortLabel-icon': {
                                                                    color: 'rgba(255,255,255,0.6) !important',
                                                                },
                                                            }}
                                                        >
                                                            {t(col.labelKey, { _: col.labelKey })}
                                                        </TableSortLabel>
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {usersLoading && (
                                            <TableRow>
                                                <TableCell colSpan={TABLE_COLUMNS.length} sx={{ color: '#FFFFFF', py: 4, textAlign: 'center' }}>
                                                    {t('module.dashboardV3.common.loading', { _: 'Loading...' })}
                                                </TableCell>
                                            </TableRow>
                                        )}

                                        {!usersLoading && list.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={TABLE_COLUMNS.length} sx={{ color: '#FFFFFF', py: 4, textAlign: 'center' }}>

                                                    {t('module.dashboardV3.common.empty', { _: 'No data' })}
                                                </TableCell>
                                            </TableRow>
                                        )}

                                        {!usersLoading &&
                                            list.map((row) => (
                                                <TableRow
                                                    key={row.id}
                                                    hover
                                                    sx={{
                                                        cursor: 'pointer',
                                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                                                        '& td': { borderBottom: '1px solid rgba(255,255,255,0.08)' },
                                                    }}
                                                    onClick={() => handleRowClick(row)}
                                                >
                                                    {TABLE_COLUMNS.map((col) => (
                                                        <TableCell
                                                            key={col.key}
                                                            align={col.align}
                                                            sx={{
                                                                color: CELL_COLOR[col.key] ?? 'rgba(255,255,255,0.85)',
                                                                fontSize: 13,
                                                                fontFamily: col.mono ? 'Roboto Mono, monospace' : undefined,
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {COPYABLE_COLUMNS[col.key] ? (
                                                                <CopyCell
                                                                    text={row[col.key as keyof typeof row] as any}
                                                                    shorten={['walletAddr', 'loginAccount', 'userId'].includes(col.key)}
                                                                    copiedText={t('module.dashboardV3.common.copied', { _: 'Copied' })}
                                                                />
                                                            ) : (
                                                                formatCellValue(col.key, row[col.key as keyof typeof row])
                                                            )}
                                                        </TableCell>
                                                    ))}
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handleRowsPerPageChange}
                                labelRowsPerPage={t('module.dashboardV3.common.rowsPerPage', { _: 'Rows per page' })}
                                labelDisplayedRows={({ from, to, count }) => `${from}–${to} / ${count}`}
                                sx={{
                                    bgcolor: '#232323',
                                    color: 'rgba(255,255,255,0.75)',
                                    '.MuiTablePagination-toolbar': { minHeight: 56, px: 2 },
                                    '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                        color: 'rgba(255,255,255,0.72)',
                                    },
                                    '.MuiTablePagination-selectIcon': { color: 'rgba(255,255,255,0.72)' },
                                    '.MuiInputBase-root': { color: 'rgba(255,255,255,0.9)' },
                                }}
                            />
                        </Paper>


                        <PermissionButton resource="dashboard-v3" action="read">
                            <ReferralDetailDialog
                                open={Boolean(dialogId)}
                                onClose={handleDialogClose}
                                meta={detail}
                                loading={detailLoading}
                                exporting={exporting}
                                productType={productType}
                                productAccent={currentProductAccent}
                                onExport={(filter) => {
                                    if (!dialogId) return;
                                    exportSubsCsv(dialogId, filter);
                                }}
                            />

                        </PermissionButton>
                    </Box>
                </DashboardContent>
            </PermissionGuard>
        </>
    );
};